/**
 * Per-browser product display order (drag to reorder on the Products page).
 * ONE list, shared by every surface that lists products — the Products page
 * AND the app rail (owner 2026-08-27: "the sidebar ordering is not
 * respecting ordering set in products page"). Ids absent from the saved
 * order (new products) sort first; deleted ids are ignored.
 *
 * `saveOrder` broadcasts a same-tab event (localStorage's `storage` event
 * only fires in OTHER tabs), so the rail re-sorts the moment a row drops.
 */

const ORDER_KEY = 'loremap.products.order';
const ORDER_EVENT = 'loremap:products-order';

/** The last read, so an unchanged saved text yields the same array. */
let snapshot: { raw: string | null; order: string[] } | null = null;

/**
 * The saved order. While the saved text is unchanged the SAME array comes
 * back, which is what `useSyncExternalStore` (the rail's switcher) needs
 * from a snapshot; a fresh array per read would re-render without end.
 */
export function loadOrder(): string[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(ORDER_KEY);
  } catch {
    raw = null;
  }
  if (snapshot !== null && snapshot.raw === raw) return snapshot.order;
  let order: string[] = [];
  try {
    const parsed = raw === null ? [] : (JSON.parse(raw) as unknown);
    order = Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    order = [];
  }
  snapshot = { raw, order };
  return order;
}

export function saveOrder(ids: string[]): void {
  try {
    window.localStorage.setItem(ORDER_KEY, JSON.stringify(ids));
  } catch {
    /* private mode etc. — order just does not persist */
  }
  window.dispatchEvent(new Event(ORDER_EVENT));
}

/** Re-sort by the saved order; unknown ids keep their relative place first. */
export function applyOrder<T extends { id: string }>(items: T[], order: string[]): T[] {
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...items].sort((a, b) => (rank.get(a.id) ?? -1) - (rank.get(b.id) ?? -1));
}

/** Fires `onChange` when this tab (or another) rewrites the saved order. */
export function subscribeOrder(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === ORDER_KEY) onChange();
  };
  window.addEventListener(ORDER_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(ORDER_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}
